import { noop } from '@mail-core/std';
import { Icon24Add } from '@vkontakte/icons';
import clsx from 'clsx';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Portal } from 'react-portal';
import { useDispatch, useSelector } from 'react-redux';
import { NEW_PORTAL_HEADER } from 'reactApp/appHelpers/configHelpers';
import {
    forceVigboLinkedAccount,
    vigboFeaturesPreviewFree,
    vigboFeaturesPreviewPaid,
} from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { useVigboTariff } from 'reactApp/hooks/useVigboTariff';
import { routeChangeSuccess } from 'reactApp/modules/router/router.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { mockVigboRootAlbum } from 'reactApp/modules/vigbo/vigbo.helpers';
import { loadVigboAlbumsListRequest } from 'reactApp/modules/vigbo/vigbo.module';
import { getVigboAlbums, getVigboLinkState, getVigboLoadingState } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import { renderNewVigboAlbumModal } from 'reactApp/sections/VigboPage/renderNewVigboAlbumModal';
import { store } from 'reactApp/store';
import { AlbumsGrid } from 'reactApp/ui/AlbumsGrid/AlbumsGrid';
import { BreadcrumbsContainer } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { Empty } from 'reactApp/ui/Empty/Empty';
import { EmptyFinishedVigbo } from 'reactApp/ui/EmptyFinishedVigbo/EmptyFinishedVigbo';
import { EmptyFolder } from 'reactApp/ui/EmptyFolder/EmptyFolder';
import { EmptyVigboFeaturesPreview } from 'reactApp/ui/EmptyVigboFeaturesPreview/EmptyVigboFeaturesPreview';
import { Footer } from 'reactApp/ui/Footer/Footer';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { UploadButton } from 'reactApp/ui/UploadDropArea/Upload/Upload';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './VigboPage.css';

export const VigboPage = memo(function VigboPage() {
    const dispatch = useDispatch();

    const accountLinkState = useSelector(getVigboLinkState);
    const isLinkedAccount = forceVigboLinkedAccount || accountLinkState === VigboAccountLinkState.linked;

    const vigboAlbums = useSelector(getVigboAlbums);
    const { isLoading: isVigboAlbumsLoading, isLoaded } = useSelector(getVigboLoadingState);
    const { isLoading: isVigboTariffLoading, isVigboActiveTariff, isVigboFinishedTariff } = useVigboTariff();

    const isLoading = isVigboAlbumsLoading || isVigboTariffLoading;

    useEffect(() => {
        const connection_vigbo = getVigboLoadingState(store.getState()).isLoaded;
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'section',
            section: 'vigbo',
            connection_vigbo,
        });
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'vigbo',
            section: 'vigbo',
            connection_vigbo,
        });
        sendPaymentGa({
            eventCategory: ECategoryGa.entered,
            action: 'page-entered',
            source: 'vigbo',
            connection_vigbo,
        });
        dispatch(routeChangeSuccess({ id: '/', storage: EStorageType.vigbo, params: {}, __isFolder: true, url: '', __parent: '' }));
        dispatch(loadVigboAlbumsListRequest());
    }, []);

    const rootAlbum = useMemo(mockVigboRootAlbum, []);
    const uploadBtnText = useMemo(
        () => (
            <div className={styles.createBttn}>
                <Icon24Add />
                <span className={styles.createBttnText}>Создать галерею</span>
            </div>
        ),
        []
    );
    const createVigboAlbum = useCallback((lowerRight: boolean) => {
        renderNewVigboAlbumModal({ navigateToAlbum: true });
        sendPaymentGa({
            eventCategory: ECategoryGa.vigbo,
            action: 'create-gallery-click',
            place: lowerRight ? 'vigbo-float-btn' : 'vigbo',
        });
    }, []);

    const isPaid = useSelector(UserSelectors.isPaidUser);
    const showFeaturesPreviewEmpty = (vigboFeaturesPreviewFree && !isPaid) || (vigboFeaturesPreviewPaid && isPaid);

    const isEmpty = vigboAlbums.length === 0 || !isLoaded || !isLinkedAccount || showFeaturesPreviewEmpty;
    const emptyContent = useMemo(() => {
        if (!isLinkedAccount && !isVigboActiveTariff) {
            return <EmptyVigboFeaturesPreview />;
        }

        if (!isLinkedAccount) {
            if (isLoaded && (isVigboActiveTariff || isVigboFinishedTariff)) {
                sendPaymentGa({
                    eventCategory: ECategoryGa.vigbo,
                    action: 'button-connection-show',
                });
            }
            return <Empty.New storage={EStorageType.vigbo} categoryType={VigboAccountLinkState.unlinked} />;
        }

        if (!isVigboActiveTariff) {
            return <EmptyFinishedVigbo />;
        }

        return (
            <>
                <EmptyFolder folder={rootAlbum} />
                <Portal>
                    <div className={styles.createBttnWrapper}>
                        <UploadButton
                            preventInteraction
                            isOpen={false}
                            text=""
                            title={uploadBtnText}
                            onMouseEnter={noop}
                            onMouseLeave={noop}
                            onClick={() => createVigboAlbum(true)}
                            onFileClick={noop}
                            onFolderClick={noop}
                            onSelectFromCloud={noop}
                            isPhone={false}
                            // tempexp-17235-start
                            isCentered={true}
                        />
                    </div>
                </Portal>
            </>
        );
    }, [isLinkedAccount, isVigboActiveTariff, rootAlbum, uploadBtnText, createVigboAlbum]);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <div
            className={clsx(styles.root, {
                [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
            })}
        >
            <div
                className={clsx(styles.wrapper, {
                    [styles.emptyWrapper]: isEmpty,
                })}
            >
                <div
                    className={clsx(styles.header, {
                        [styles.hiddenToolbar]: !isVigboActiveTariff && !isLinkedAccount,
                    })}
                >
                    <BreadcrumbsContainer />
                </div>

                {isEmpty ? (
                    <div className={styles.emptyContent}>{emptyContent}</div>
                ) : (
                    <div className={styles.grid_container}>
                        <AlbumsGrid
                            handleCreateClick={
                                !isVigboActiveTariff
                                    ? noop
                                    : () => {
                                          createVigboAlbum(false);
                                      }
                            }
                            disableCreateButton={!isVigboActiveTariff}
                            createButtonText="Создать галерею Vigbo"
                            albums={vigboAlbums}
                            qaTitle="vigbo-galleries"
                        />
                    </div>
                )}

                <Footer storage={EStorageType.vigbo} isAbsolutePos={true} />
            </div>
        </div>
    );
});
